import { format, isAfter, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import { Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import "modules/ParkingGateCodesDashboard/components/AddUpdateParkingGateCodeModal/AddUpdateParkingGateCodeModal.scss";
import { AddUpdateParkingGateCodeForm } from "modules/ParkingGateCodesDashboard/components/AddUpdateParkingGateCodeModal/components";
import { useCreateParkingGateCodeMutation, useUpdateParkingGateCodeMutation } from "modules/ParkingGateCodesDashboard/components/AddUpdateParkingGateCodeModal/hooks";
import { toISOString } from "utils/dateHelperFunctions";

import type { ParkingGateCode } from "models";

type AddUpdateParkingGateCodeModalProps = {
  isOpen: boolean;
  parkingGateCode: ParkingGateCode | null;
  onClose: () => void;
};

export type AddUpdateParkingGateCodeFormValues = Pick<ParkingGateCode, "dealer_location_id" | "start_at" | "end_at" | "pin">;

const FormHelper = createFormHelper<AddUpdateParkingGateCodeFormValues>();

export const AddUpdateParkingGateCodeModal = ({ isOpen, onClose, parkingGateCode }: AddUpdateParkingGateCodeModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);

  const { selectedLocation } = useSelectedLocation();
  const { mutateAsync: createParkingGateCode, isPending: isCreateParkingGateCodePending } = useCreateParkingGateCodeMutation();
  const { mutateAsync: updateParkingGateCode, isPending: isUpdateParkingGateCodePending } = useUpdateParkingGateCodeMutation();

  const defaultValues = useMemo(
    () => ({ pin: "", dealer_location_id: selectedLocation?.id || 0, start_at: toISOString(new Date()), end_at: "" }),
    [selectedLocation?.id]
  );

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const isFormDataInvalid = (values: AddUpdateParkingGateCodeFormValues) =>
    !values.start_at || !values.end_at || !values.pin || isAfter(startOfDay(values.start_at), startOfDay(values.end_at));

  const handleSubmit = async (values: AddUpdateParkingGateCodeFormValues) => {
    const hasErrors = isFormDataInvalid(values);
    setHasErrors(hasErrors);
    if (hasErrors) return;

    if (parkingGateCode)
      await updateParkingGateCode({
        ...parkingGateCode,
        ...values,
        start_at: format(values.start_at, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ),
        end_at: format(values.end_at, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ),
        updated_on: toISOString(new Date())
      });
    else {
      if (!selectedLocation?.id) return;

      await createParkingGateCode({
        ...values,
        start_at: format(values.start_at, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ),
        end_at: format(values.end_at, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ),
        id: -1,
        dealer_location_id: selectedLocation.id,
        created_on: toISOString(new Date()),
        updated_on: toISOString(new Date())
      });
    }

    handleClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} closeOnDimmerClick={false}>
      <div className="AddUpdateParkingGateCodeModal">
        <FormHelper.Form onSubmit={handleSubmit} defaultValues={parkingGateCode || defaultValues}>
          <AddUpdateParkingGateCodeForm
            hasErrors={hasErrors}
            isPending={isCreateParkingGateCodePending || isUpdateParkingGateCodePending}
            parkingGateCode={parkingGateCode}
            onClose={handleClose}
          />
        </FormHelper.Form>
      </div>
    </Modal>
  );
};
